import { useState } from 'react';
import { Loader2, Bug, Lightbulb, HelpCircle, CheckCircle } from 'lucide-react';
import { Modal } from './Modal';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../lib/posthog';

type FeedbackType = 'bug' | 'suggestion' | 'question';

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

const typeConfig: { value: FeedbackType; icon: typeof Bug; emoji: string }[] = [
  { value: 'bug', icon: Bug, emoji: '' },
  { value: 'suggestion', icon: Lightbulb, emoji: '' },
  { value: 'question', icon: HelpCircle, emoji: '' },
];

export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const { t } = useTranslation();
  const [type, setType] = useState<FeedbackType>('suggestion');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setError('');
    setLoading(true);

    const { error: fnError } = await supabase.functions.invoke('send-feedback', {
      body: { type, message: message.trim() },
    });

    setLoading(false);
    if (fnError) {
      setError(t('feedback.error'));
    } else {
      trackEvent('feedback_submitted', { type });
      setSuccess(true);
    }
  };

  const handleClose = () => {
    setType('suggestion');
    setMessage('');
    setSuccess(false);
    setError('');
    onClose();
  };

  const typeLabel = (v: FeedbackType) => {
    const map: Record<FeedbackType, string> = {
      bug: t('feedback.typeBug'),
      suggestion: t('feedback.typeSuggestion'),
      question: t('feedback.typeQuestion'),
    };
    return map[v];
  };

  return (
    <Modal open={open} onClose={handleClose} title={t('feedback.title')}>
      {success ? (
        <div className="text-center py-4">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-emerald-700 leading-relaxed">
            {t('feedback.success')}
          </p>
          <button
            onClick={handleClose}
            className="mt-6 w-full bg-stone-900 text-white py-4 rounded-2xl font-bold text-sm hover:bg-stone-800 transition-colors"
          >
            OK
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Type selector */}
          <div className="flex gap-2">
            {typeConfig.map(({ value, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setType(value)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl text-sm font-bold transition-all ${
                  type === value
                    ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-500'
                    : 'bg-stone-50 text-stone-500 border-2 border-transparent hover:border-stone-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {typeLabel(value)}
              </button>
            ))}
          </div>

          {/* Message */}
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={t('feedback.messagePlaceholder')}
            rows={4}
            className="w-full px-5 py-4 bg-stone-50 rounded-2xl border border-stone-200 text-sm font-semibold text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all resize-none"
          />

          {error && <p className="text-sm font-semibold text-rose-500">{error}</p>}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={loading || !message.trim()}
            className="w-full bg-emerald-600 text-white py-4 rounded-2xl font-bold text-sm hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('feedback.send')}
          </button>
        </div>
      )}
    </Modal>
  );
}
